/**
 * Cálculo puro de totales de cobro (sin store ni RN). Usado por la UI y tests en Node.
 */
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';

export type PosCheckoutLineTotalInput = {
  price: number;
  quantity: number;
  /** Precio unitario efectivo tras promos; default = `price`. */
  effectiveUnitPrice?: number;
  lineDiscountTotal?: number;
};

export type PosCheckoutPaymentAmountInput = { amount: number | string };

export function toPaymentAmountNumber(amount: number | string): number {
  if (typeof amount === 'number') return Number.isFinite(amount) ? amount : 0;
  return parseDecimalInput(amount, { min: 0 }) ?? 0;
}

export type PosCheckoutTotalsResult = {
  subtotal: number;
  discountTotal: number;
  total: number;
  totalIngresado: number;
  vuelto: number;
  saldoPendiente: number;
};

export function computePosCheckoutTotals(
  lines: PosCheckoutLineTotalInput[],
  paymentDrafts: PosCheckoutPaymentAmountInput[]
): PosCheckoutTotalsResult {
  const { subtotal, total, discountTotal } = lines.reduce(
    (acc, item) => {
      const unitOriginal = item.price;
      const unitEffective = item.effectiveUnitPrice ?? unitOriginal;
      const lineTotal = unitOriginal * item.quantity;
      const lineEffective = unitEffective * item.quantity;
      acc.subtotal += lineTotal;
      acc.total += lineEffective;
      acc.discountTotal += item.lineDiscountTotal ?? Math.max(0, lineTotal - lineEffective);
      return acc;
    },
    { subtotal: 0, total: 0, discountTotal: 0 }
  );
  const totalIngresado = paymentDrafts.reduce((sum, draft) => sum + toPaymentAmountNumber(draft.amount), 0);
  // Redondeo a centavos: promos porcentuales dejan epsilons de flotante
  // (p.ej. 1805.7599999999998) que mostrarían «Vuelto $0,00» espurio.
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const vuelto = round2(Math.max(totalIngresado - total, 0));
  const saldoPendiente = round2(Math.max(total - totalIngresado, 0));
  return { subtotal, discountTotal: Math.max(0, discountTotal), total, totalIngresado, vuelto, saldoPendiente };
}
